import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';

import './header.scss';

import navbarLogo from '../../assets/header/navbarLogo.png';
import phone from '../../assets/icons/phone.png';

import { scrollToSection } from '../../utils/scrollToSection';
import { withLang } from '../../utils/withLang';
import LanguageChange from '../languageChange/languageChange';
import { useTranslation } from 'react-i18next';
import { trackCallConversion } from '../../utils/googleAdsConversion';

const Header = ({ myRef }) => {
  const ref = useRef(null);
  const { t } = useTranslation();
  const location = useLocation();

  const [expanded, setExpanded] = useState(false);
  const [practiceOpen, setPracticeOpen] = useState(false);

  const sticky = useStickyHeader(76);
  const headerClasses = `header ${sticky ? 'sticky' : ''}`;

  // Helper using global utility
  const langPath = path => withLang(location.pathname, path);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    const body = document.querySelector('.myBody');
    if (!body) return;

    const closeMenu = () => setExpanded(false);

    if (expanded) {
      body.style.overflow = 'hidden';
      body.addEventListener('click', closeMenu);
      body.addEventListener('touchstart', closeMenu);
      body.addEventListener('scroll', closeMenu);
    } else {
      body.style.overflow = 'auto';
      body.removeEventListener('click', closeMenu);
      body.removeEventListener('touchstart', closeMenu);
      body.removeEventListener('scroll', closeMenu);
    }

    return () => {
      body.removeEventListener('click', closeMenu);
      body.removeEventListener('touchstart', closeMenu);
      body.removeEventListener('scroll', closeMenu);
    };
  }, [expanded]);

  // Sticky header hook
  function useStickyHeader(offset = 0) {
    const [stick, setStick] = useState(false);

    useLayoutEffect(() => {
      const handleScroll = () => setStick(window.scrollY > offset);
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }, [offset]);

    return stick;
  }

  const clickOnLink = id => {
    myRef.current = id; // ✅ store the section id
    scrollToSection(id);
    setExpanded(false);
    setPracticeOpen(false);
  };

  return (
    <div ref={ref} className={headerClasses}>
      <Navbar
        expand="lg"
        bg="dark"
        data-bs-theme="dark"
        className="bg-body-tertiary"
        expanded={expanded}
      >
        <Container fluid>
          {/* Logo (crawlable link) */}
          <Navbar.Brand
            as={NavLink}
            to={langPath('/')}
            onClick={() => {
              setExpanded(false);
              setPracticeOpen(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            aria-label="Home"
          >
            <div className="logo">
              {/* This logo is the page's LCP element (confirmed via PageSpeed
                  Insights) and is always visible above the fold, so it must
                  never be lazy-loaded - LazyLoadImage was delaying it behind
                  a JS-driven IntersectionObserver instead of letting the
                  browser's preload scanner fetch it immediately, which was
                  adding 1s+ to LCP. Plain eager img + fetchpriority instead. */}
              <img
                src={navbarLogo}
                alt="Avagyan Law Firm Home"
                width={180}
                height={60}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </Navbar.Brand>

          {/* Mobile phone CTA (descriptive + crawlable) */}
          <div className="mobile-phone-call">
            <a
              href="tel:+19169999909"
              aria-label="Call Avagyan Law Firm"
              onClick={trackCallConversion}
            >
              {/* Same reasoning as the logo above: this icon sits in the
                  header, so it's always visible above the fold on every
                  page, on every device width - it's never actually "below
                  the fold" for LazyLoadImage to legitimately defer. Because
                  react-snap's crawl waits long enough for the
                  IntersectionObserver to fire and swap the real <img> in
                  before it captures the page, the prerendered HTML always
                  has the real <img>. A real visitor's FIRST paint doesn't
                  get that same head start: LazyLoadImage's initial render is
                  a placeholder <span>, a different element type than the
                  prerendered <img>, which is a structural mismatch
                  hydration cannot reconcile - "Minified React error #418"
                  on every single page load, every language, confirmed via
                  build/404.html directly. Plain eager img avoids the
                  placeholder state entirely. */}
              <img src={phone} alt="phone" width={24} height={24} aria-hidden="true" loading="eager" decoding="async" />
            </a>
          </div>

          {/* Mobile toggle */}
          <Navbar.Toggle
            aria-controls="navbarScroll"
            className="mobile-navbar-icon"
            onClick={() => {
              setExpanded(!expanded);
              setPracticeOpen(false);
            }}
          >
            <span className="icon-bar" />
            <span className="icon-bar" />
            <span className="icon-bar" />
          </Navbar.Toggle>

          {/* Navigation */}
          <Navbar.Collapse id="navbarScroll" className="collapseLinks">
            <Nav className="mx-auto my-2 my-lg-0 d-lg-flex align-items-center d-block" navbarScroll>
              <div className="links d-flex align-items-lg-center align-items-start flex-lg-row flex-column">
                <NavLink
                  to={langPath('/about')}
                  onClick={() => clickOnLink('about')}
                  className="navlink"
                >
                  {t('header.About')}
                </NavLink>

                <div className="practice-areas-dropdown">
                  <button
                    type="button"
                    className="nav-link dropdown-toggle"
                    aria-haspopup="true"
                    aria-expanded={practiceOpen ? 'true' : 'false'}
                    onClick={e => {
                      e.stopPropagation();
                      setPracticeOpen(prev => !prev);
                    }}
                  >
                    {t('header.PracticeAreas')}
                  </button>

                  <div className={`dropdown-menu ${practiceOpen ? 'show' : ''} show-on-hover`}>
                    <NavLink
                      className="dropdown-item"
                      to={langPath('/immigration')}
                      onClick={() => {
                        setExpanded(false);
                        setPracticeOpen(false);
                      }}
                    >
                      {t('header.Immigration')}
                    </NavLink>

                    <NavLink
                      className="dropdown-item"
                      to={langPath('/personalinjury')}
                      onClick={() => {
                        setExpanded(false);
                        setPracticeOpen(false);
                      }}
                    >
                      {t('header.PersonalInjury')}
                    </NavLink>

                    <NavLink
                      className="dropdown-item"
                      to={langPath('/lemonlaw')}
                      onClick={() => {
                        setExpanded(false);
                        setPracticeOpen(false);
                      }}
                    >
                      {t('header.LemonLaw')}
                    </NavLink>

                    {/*<NavLink*/}
                    {/*  className="dropdown-item"*/}
                    {/*  to={langPath('/workerscomp')}*/}
                    {/*  onClick={() => {*/}
                    {/*    setExpanded(false);*/}
                    {/*    setPracticeOpen(false);*/}
                    {/*  }}*/}
                    {/*>*/}
                    {/*  {t('header.WorkersComp')}*/}
                    {/*</NavLink>*/}

                    <NavLink
                      className="dropdown-item"
                      to={langPath('/employmentlaw')}
                      onClick={() => {
                        setExpanded(false);
                        setPracticeOpen(false);
                      }}
                    >
                      {t('header.EmploymentLaw')}
                    </NavLink>
                  </div>
                </div>

                <NavLink
                  to={langPath('/team')}
                  onClick={() => clickOnLink('team')}
                  className="navlink"
                >
                  {t('header.Team')}
                </NavLink>

                <NavLink
                  to={langPath('/blog')}
                  onClick={() => clickOnLink('blog')}
                  className="navlink"
                >
                  {t('header.Blog')}
                </NavLink>

                <NavLink
                  to={langPath('/contact-us')}
                  onClick={() => {
                    setExpanded(false);
                    setPracticeOpen(false);
                  }}
                  className="navlink"
                >
                  {t('header.Contact us')}
                </NavLink>
              </div>
            </Nav>

            <div className="desktop-phone-call me-3 d-none d-lg-block">
              <a
                href="tel:+19169999909"
                aria-label="Call Avagyan Law Firm at +1 (916) 999-9909"
                onClick={trackCallConversion}
                className="fw-bold"
              >
                +1 (916) 999-9909
              </a>
            </div>

            {/* Language Switcher */}
            <LanguageChange />
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </div>
  );
};

export default Header;
